@import "../../css/button.css";

:host {
  all: initial;
  font-family: var(--zapier-font-family);
  color: var(--zapier-text);
  fill: var(--zapier-text-60);
  font-size: var(--zapier-font-size-16px);
  margin: 0;
  padding: 0;
  border: 1px solid var(--zapier-border-color);

  position: relative;
  display: block;
  align-items: center;
  background: var(--zapier-bg-5);
  border-radius: calc(var(--zapier-grid-unit) / 2);
  box-shadow: 0 6px 3px -3px rgb(0 0 0 / 10%);
}

:host(:focus-visible) {
  outline: 2px solid var(--zapier-brand);
  outline-offset: -2px;
}

:host(:hover) {
  outline: 1px solid var(--zapier-brand);
  outline-offset: -1px;
}

/* We found that Safari wasn't taking into consideration the container queries
   when using the Host element as the container, so we had to re-write it and make
   changes to the elements adding a wrapper element below the Host. This we mainly
   needed due to Safari issue. On other browsers, we were able to use the Host element as the container.
   Check Sage's CodePen here https://codepen.io/wavebeem/pen/RwdVvJO
*/
.prefab-row-host-container {
  container: prefab-row-host-container;
  container-type: inline-size;
}

ul {
  margin: 0;
  padding: 0;
}

a {
  color: inherit;
  text-decoration: none;
}

p {
  color: var(--zapier-text-80);
  font-size: var(--zapier-font-size-14px);
  letter-spacing: -0.2px;
}

h3,
a,
p {
  padding: 0;
  margin: 0;
  margin-block-start: 0;
  margin-block-end: 0;
}

h3 {
  display: flex;
  justify-content: center;
  align-items: center;
}

.zap {
  display: grid;
  padding: calc(1.5 * var(--zapier-grid-unit));
  column-gap: calc(2 * var(--zapier-grid-unit));
  row-gap: calc(1 * var(--zapier-grid-unit));
  grid-template-columns: 1fr;
  grid-template-areas:
    "icons       "
    "body        "
    "footer-start";
}

.zap:not([data-zap-state]) {
  row-gap: calc(0.5 * var(--zapier-grid-unit));
}

.zap[data-zap-state] {
  grid-template-columns: 1fr 1fr;
  grid-template-areas:
    "icons         zap-state   "
    "body          body        "
    "footer-start  footer-start"
    "footer-end    footer-end  ";
}

.zap[data-cta] {
  grid-template-columns: 1fr;
  grid-template-areas:
    "icons       "
    "body        "
    "footer-start"
    "cta         ";
}

.body {
  grid-area: body;
  display: flex;
  flex: auto;
  justify-content: space-between;
  align-items: center;
}

/* The focus styles here are already covered via the focus styles for the entire
host component */
.title-link:focus-visible {
  outline: none;
}

/* Comment out this rule while debugging layout issues since it makes it harder
to use the DOM inspector to click on things with this element covering
everything up */
.title-link::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* TODO: refactor line-height in TPS-1526 */
.zap h3 {
  font-size: var(--zapier-font-size-16px);
  font-weight: 600;
  line-height: 1.5;
  color: var(--zapier-text-80);
}

.icon-container {
  grid-area: icons;
  display: flex;
  align-items: center;
  gap: calc(var(--zapier-grid-unit) / 2);
}

.icon {
  padding: calc(var(--zapier-grid-unit) / 2);
  background: var(--zapier-icon-bg);
  border: 1px solid var(--zapier-icon-border-color);
  border-radius: calc(var(--zapier-grid-unit) / 2);
  background-clip: padding-box;
  box-sizing: border-box;
}

.icon img {
  width: calc(var(--zapier-grid-unit) * 2.8);
  height: calc(var(--zapier-grid-unit) * 2.8);
  display: block;
  object-fit: contain;
}

.card {
  box-sizing: border-box;
}

.zap-state {
  grid-area: zap-state;
  box-sizing: border-box;
  justify-self: end;
  align-self: center;
  padding: calc(0.25 * var(--zapier-grid-unit)) var(--zapier-grid-unit);
  min-width: 50px;
  font-size: var(--zapier-font-size-14px);
  font-weight: 700;
  text-transform: uppercase;
  color: var(--zapier-zap-state-off-text);
  background-color: var(--zapier-zap-state-off-bg);
  border: 1px solid var(--zapier-border-color);
  text-align: center;
  border-radius: calc(var(--zapier-grid-unit) * 3);
}

.zap-state[data-enabled] {
  color: var(--zapier-zap-state-on-text);
  background-color: var(--zapier-zap-state-on-bg);
}

.zap-state,
.cta {
  flex: none;
}

.cta {
  grid-area: cta;
  align-self: center;
  /* TODO: We should make the button trigger underlying `<a>` tag rather than
  makign it bypass `pointer-events`, because that removes the mouse hover
  behavior as well. If you're seeing this comment, please consider changing it.
  And add a test that clicks this button so we don't have this regression again.
  */
  pointer-events: none;
}

.cta[hidden] {
  display: none;
}

/* Container queries */
@container prefab-row-host-container (min-width: 550px) {
  .zap {
    grid-template-columns: max-content 1fr;
    grid-template-areas:
      "icons  body        "
      "icons  footer-start";
  }

  .zap[data-zap-state] {
    grid-template-columns: max-content 1fr max-content;
    grid-template-areas:
      "icons  body          zap-state "
      "icons  footer-start  footer-end";
  }

  .zap[data-cta] {
    grid-template-columns: max-content 1fr max-content;
    grid-template-areas:
      "icons  body          cta"
      "icons  footer-start  cta";
  }

  [data-cta] {
    grid-template-columns: min-content auto max-content;
  }
}
